import { Component } from "@eusoft/webapp-core";
import { Text, forModel } from "@eusoft/webapp-jsx";
import { MaterialIcon, MaterialIconName, MaterialIconVariant, ViewNode } from "@eusoft/webapp-ui";
import { SyncMode, controls } from "../services/Controls";
import {  synthCatalog } from "../player/SynthCatalog";
import { IButtonOption, ToolbarButton } from "./ToolbarButton";
import "./Toolbar.scss";

function icon(name: MaterialIconName, variant?: MaterialIconVariant) {
    return <MaterialIcon name={name} variant={variant} />;
}

function text(v: string) {

    return <span><Text src={v} /></span>;
}

export class Toolbar extends Component {

    constructor() {
        super();

        this.init(Toolbar, {
            template: forModel((m: this) => <div className={m.className}>
                {m.content.forEach(a => a)}
            </div>)
        });
    }

    addPush(icon: ViewNode, action?: () => void) {
        return this.addButton(new ToolbarButton({
            type: "push",
            executeAsync: action,
            content: v => icon
        }));
    }

    addDropDown<T>(items: IButtonOption<T>[], icon: ViewNode, onValueChanged?: (v: T) => void) {
        return this.addButton(new ToolbarButton({
            items,
            type: "drop-down",
            onValueChanged,
            showValue: true,
            content: v => icon
        }));
    }

    addDropToogle<T>(items: IButtonOption<T>[], onIcon: ViewNode, offIcon?: ViewNode, onCheckedChanged?: (value: boolean) => void, onValueChanged?: (value: T)=> void) {
        return this.addButton(new ToolbarButton({
            items,
            type: "toggle",
            onValueChanged,
            onCheckedChanged,
            content: (v, c) => c || !offIcon ? onIcon : offIcon
        }));
    }

    addToggle(onIcon: ViewNode, offIcon?: ViewNode, action?: (value: boolean) => void) {
        return this.addButton(new ToolbarButton({
            type: "toggle",
            onCheckedChanged: action,
            content: (v, c) => c || !offIcon ? onIcon : offIcon
        }));
    }

    addDivider() {

        this.content.push(<div className="sep" />);
    }

    addButton<TBtn extends ToolbarButton<TValue>, TValue>(button: TBtn) {

        this.content.push(button);
        return button;
    }

    protected async updateState() {

        const state = controls.getState();

        this.synth.value = state.synthId;
        this.synth.checked = !state.isSongMute;
        this.sync.checked = state.isSyncOn;
        this.sync.value = state.syncMode;
        this.showSong.checked = state.showSong;
        this.showPitch.checked = state.showPitch;
        this.isLoop.checked = state.isLoop;
        this.songKey.value = state.songKey;
        this.songSpeed.value = state.songSpeed;

        if (state.micId)
            this.mic.value = state.micId;

        this.play.active = controls.state == "play" || controls.state == "record";
        this.play.disabled = controls.state == "play";
       
        this.record.checked = controls.state == "record";

        this.stop.disabled = controls.state == "stop";
        this.pause.disabled = controls.state != "play" && controls.state != "record";

        this.mic.checked = !state.isMicMute;
    }

   async loadAsync() {

        const syncs: IButtonOption<SyncMode>[] = [];
        syncs.push({ text: "song", value: "song" });
        syncs.push({ text: "pitch", value: "pitch" });


        const speeds = [0.1, 0.2, 0.5, 0.7, 0.9, 1, 1.5, 2, 2.5, 3].map(a => ({
            value: a,
            text: (a * 100) + "%"
        }));

        const keys = [-4, -3, -2, -1, 0, 1, 2, 3, 4].map(a => ({
            value: a,
            text: a > 0 ? "+" + a : a.toString()
        }));

        controls.beginUpdate(); 

        const state = controls.getState();

        this.songSpeed = this.addDropDown(speeds, icon("speed"), v => controls.songSpeed = v);

        this.songKey = this.addDropDown(keys, icon("music_note"), v => controls.songKey = v);

        this.mic = this.addDropToogle([], icon("mic"), icon("mic_off"),
            v => controls.setIsMicMuteAsync(!v),
            v => controls.setMicAsync(v));

        this.addDivider();

        this.record = this.addToggle(icon("fiber_manual_record", "filled"), undefined, v => {
            if (v)
                controls.startRecordAsync();
            else if (controls.state === "record")
                controls.stopRecord();
        });

        this.play = this.addPush(icon("play_arrow"), () => controls.playAsync());
        this.pause = this.addPush(icon("pause"), () => controls.pauseAsync());
        this.stop = this.addPush(icon("stop"), () => controls.stopAsync());

        this.addDivider();

        this.isLoop =  this.addToggle(icon("repeat"), undefined, v => controls.isLoop = v);
        this.addPush(text("{"), () => controls.markStartLoop());
        this.addPush(text("}"), () => controls.markEndLoop());

        this.addDivider();

        this.showPitch = this.addToggle(icon("show_chart"), undefined, v => controls.showPitch = v);

        this.showSong = this.addToggle(icon("queue_music"), undefined, v => controls.showSong = v);

        this.synth = this.addDropToogle([], icon("volume_up"), icon("volume_off"),
            v => controls.isSongMute = !v,
            v => v ? controls.setSynthIdAsync(v) : undefined);

        this.sync = this.addDropToogle(syncs, icon("sync"), icon("sync_disabled"),
            v => controls.isSyncOn = v,
            v => controls.syncMode = v,
        );

        await this.refreshOptionsAsync();

        controls.addEventListener("stateChanged", () => this.updateState());

        await controls.setStateAsync(state);

        controls.endUpdate();

        this.play.name = "play";
        this.stop.name = "stop";
        this.record.name = "record";

        return true;
    }

    async refreshOptionsAsync() {

        if (localStorage.getItem("permissions") != "true")
            return;

        const synthDevices = await synthCatalog.enumDevicesAsync();

        const synths = synthDevices.map(a => ({
            text: a.deviceId,
            value: a.deviceId
        }));

        this.synth.items = synths;

        const mics = (await controls.getMicsAsync()).map(a => ({
            text: a.label,
            value: a.deviceId
        }));


        this.mic.items = mics;

    }

    songSpeed: ToolbarButton<number>;
    songKey: ToolbarButton<number>;
    mic: ToolbarButton<string>;
    record: ToolbarButton<unknown>;
    play: ToolbarButton<unknown>;
    pause: ToolbarButton<unknown>;
    stop: ToolbarButton<unknown>;
    isLoop: ToolbarButton<unknown>;
    showPitch: ToolbarButton<unknown>;
    showSong: ToolbarButton<unknown>;
    synth: ToolbarButton<string>;
    sync: ToolbarButton<unknown>;

    content: (ToolbarButton<unknown> | ViewNode)[] = [];
}